import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart attribute that records the buyer's acceptance of the customs clause.
 *
 * The value is an ISO timestamp rather than a boolean: Shopify carries cart attributes through
 * checkout and onto the order, so this is what makes the consent provable per order instead of
 * being a UI-only gate that leaves no trace.
 */
const CONSENT_ATTRIBUTE = 'customs_consent';

/**
 * Gates every route out of the cart behind an explicit acceptance of the customs clause.
 *
 * Two routes exist and both are handled here:
 * - the theme's own checkout button, which is disabled until the clause is accepted;
 * - the accelerated checkout buttons, which Shopify renders inside iframes. A click on those
 *   cannot be intercepted, so the whole container is hidden instead.
 *
 * Routes that bypass the cart entirely (a direct /checkout link, a cart permalink, the
 * abandoned-checkout email) are out of reach from the theme and are NOT covered.
 */
class CustomsConsent extends Component {
  requiredRefs = ['input'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.input.addEventListener('change', this.#handleChange);
    this.#applyState(this.refs.input.checked);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.input.removeEventListener('change', this.#handleChange);
    this.#activeFetch?.abort();
  }

  /**
   * The controls that must not be reachable before the clause is accepted.
   *
   * Scoped to the surrounding `.cart__ctas` because the cart page and the cart drawer both
   * render this snippet, and the checkout button carries a hardcoded id in each.
   */
  get #gatedControls() {
    const scope = this.closest('.cart__ctas') ?? this.parentElement;

    return {
      checkoutButton: scope?.querySelector('[name="checkout"]') ?? null,
      expressButtons: scope?.querySelector('.additional-checkout-buttons') ?? null,
    };
  }

  #handleChange = () => {
    const accepted = this.refs.input.checked;

    this.#applyState(accepted);
    this.#persist(accepted);
  };

  /**
   * Opens or closes the gate.
   *
   * @param {boolean} accepted - Whether the clause is currently accepted.
   */
  #applyState(accepted) {
    const { checkoutButton, expressButtons } = this.#gatedControls;

    if (checkoutButton instanceof HTMLButtonElement) {
      // An empty cart has its own reason to stay disabled, so acceptance must not override it.
      checkoutButton.disabled = !accepted || this.dataset.cartEmpty === 'true';
    }

    if (expressButtons instanceof HTMLElement) {
      expressButtons.hidden = !accepted;
    }

    this.toggleAttribute('accepted', accepted);
  }

  /**
   * Records the acceptance on the cart so that it reaches the order.
   *
   * If the write fails the gate is closed again and the checkbox is cleared: letting the buyer
   * through while the record is missing would defeat the point of the checkbox.
   *
   * @param {boolean} accepted - Whether the clause is currently accepted.
   */
  async #persist(accepted) {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.#setError(false);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: { [CONSENT_ATTRIBUTE]: accepted ? new Date().toISOString() : '' },
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      if (accepted) {
        this.refs.input.checked = false;
        this.#applyState(false);
      }

      this.#setError(true);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {boolean} visible - Whether the failure message should be shown.
   */
  #setError(visible) {
    const { error } = this.refs;

    if (error instanceof HTMLElement) error.hidden = !visible;
  }
}

if (!customElements.get('tll-customs-consent')) {
  customElements.define('tll-customs-consent', CustomsConsent);
}
